// features/workstations/think/thread-card.js
// ═══════════════════════════════════════════════════════════
// One thread card on the Think tab homepage.
//
// Click body → opens the thread.
// Three-dot menu in corner: Rename | Delete (with confirm).
// ═══════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';

export default function ThreadCard({ thread, onOpen, onRename, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const dateLabel = formatDateLabel(thread.created_at);
  const lastActivity = thread.last_message_at || thread.last_opened_at || thread.created_at;
  const lastLabel = relTime(lastActivity);
  const archived = !!thread.archived_at;

  return (
    <div className="ws-thread-card" onClick={onOpen}>
      <div className="ws-thread-card-row1">
        <span className="ws-thread-card-date">{dateLabel}</span>
        <div ref={menuRef} className="ws-thread-card-menu-wrap" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="ws-thread-card-menu-trigger"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Thread menu"
          >
            ⋮
          </button>
          {menuOpen && (
            <div className="ws-thread-card-menu" role="menu">
              <button type="button" className="ws-thread-card-menu-item" onClick={() => { setMenuOpen(false); onRename?.(); }}>
                Rename
              </button>
              <button type="button" className="ws-thread-card-menu-item ws-thread-card-menu-danger" onClick={() => { setMenuOpen(false); onDelete?.(); }}>
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="ws-thread-card-title">{thread.title || 'Untitled'}</div>

      <div className="ws-thread-card-summary">
        {thread.summary || (
          <span className="ws-thread-card-summary-empty">
            No summary yet — open the thread to write your first message.
          </span>
        )}
      </div>

      <div className="ws-thread-card-footer">
        <span>{thread.message_count || 0} msg{thread.message_count === 1 ? '' : 's'}</span>
        <span>·</span>
        <span>{lastLabel}</span>
        {archived && <><span>·</span><span className="ws-thread-card-archived">archived</span></>}
      </div>
    </div>
  );
}

function formatDateLabel(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase();
  } catch { return ''; }
}

function relTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
